import { inject, Injectable } from "@angular/core";
import { CrmCompany, CrmContact, CrmCustomer, CrmEntry } from "@domain/crm/crm";
import { ContactPerson } from "@domain/project/contact-person";
import { Customer } from "@domain/project/customer";
import { ProjectFormInitialValues } from "@domain/project/project-form-initial-values";
import type { ScopedProject } from "@domain/project/project-guards";
import { TemplateService } from "@domain/project/template/template.service";
import { User } from "@domain/user/user";
import { UserService } from "@domain/user/user.service";
import { environment } from "@environments/environment";
import { BusinessScope } from "@lib/constants/business-scope";
import { filter, map, Observable, switchMap } from "rxjs";
import { take } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class CrmProjectMapperService {
  private userService = inject(UserService);
  private templateService = inject(TemplateService);

  private readonly EMPTY_STRING = "" as const;
  createProjectFromCrmEntry(crmEntry: CrmEntry): Observable<ProjectFormInitialValues> {
    return this.userService.user$.pipe(
      take(1),
      filter((user): user is User & { scope: BusinessScope } => user.scope !== undefined),
      switchMap((user) =>
        this.templateService.getGlobalTemplate(user.scope).pipe(
          map((templateJson: any) => {
            const statuses = templateJson?.properties?.project?.details?.information?.statuses ?? [];
            const initialStatus = statuses.find((status: { initial?: boolean }) => status.initial === true);
            const statusName = initialStatus?.text as string;
            const initialCountry = templateJson?.supportedCountries?.includes(user.country) ? user.country : undefined;
            const opportunityId = this.getOpportunityId(crmEntry);

            return {
              crmEntryId: crmEntry.id,
              name: this.normalizeOptionalString(crmEntry.name),
              status: statusName,
              country: initialCountry,
              opportunityId,
              opportunityLink: this.getOpportunityLink(crmEntry, opportunityId),
              customer: this.mapCrmCompanyToCustomer(this.resolveCustomerFromCrmEntry(crmEntry)),
              contactPerson: this.mapCrmContactToCustomer(crmEntry.contact),
            };
          }),
        ),
      ),
    );
  }

  overwriteProjectWithCrmEntry(project: ScopedProject, crmEntry: CrmEntry) {
    project.crmEntryId = crmEntry.id;
    project.name = this.normalizeOptionalString(crmEntry.name) ?? project.name;
    project.opportunityId = this.getOpportunityId(crmEntry);
    project.opportunityLink = this.getOpportunityLink(crmEntry, project.opportunityId);
    this.mapCrmCompanyToCustomer(this.resolveCustomerFromCrmEntry(crmEntry), project.customer);
    this.mapCrmContactToCustomer(crmEntry.contact, project.contactPerson);
    project.lastModified = new Date();
  }

  private mapCrmCompanyToCustomer(crmCompany?: CrmCompany | CrmCustomer, customer?: Customer): Customer {
    const result: Customer = customer ?? new Customer();

    result.name = crmCompany?.name ?? result.name ?? this.EMPTY_STRING;
    result.customerNumber = crmCompany?.accountNumber ?? result.customerNumber ?? this.EMPTY_STRING;
    result.street = crmCompany?.streetAndHouseNumber ?? result.street ?? this.EMPTY_STRING;
    result.zipCode = crmCompany?.postalCode ?? result.zipCode ?? this.EMPTY_STRING;
    result.city = crmCompany?.city ?? result.city ?? this.EMPTY_STRING;
    return result;
  }

  private mapCrmContactToCustomer(crmContact?: CrmContact, contactPerson?: ContactPerson): ContactPerson {
    const result: ContactPerson = contactPerson ?? new ContactPerson();
    result.firstname = crmContact?.firstname ?? result.firstname ?? "";
    result.lastname = crmContact?.lastname ?? result.lastname ?? "";
    result.department = crmContact?.department ?? result.department ?? "";
    result.email = crmContact?.email ?? result.email ?? "";
    result.phoneNumber = crmContact?.mobilePhone ?? crmContact?.telephone ?? result.phoneNumber ?? this.EMPTY_STRING;
    return result;
  }

  private resolveCustomerFromCrmEntry(crmEntry: CrmEntry): CrmCompany | CrmCustomer | undefined {
    return crmEntry.customerShippedTo ?? crmEntry.company ?? crmEntry.customerSoldTo ?? crmEntry.endUser;
  }

  private getOpportunityId(crmEntry: CrmEntry): string | undefined {
    return this.normalizeOptionalString(crmEntry.opportunityId);
  }

  private getOpportunityLink(crmEntry: CrmEntry, opportunityId?: string): string | undefined {
    return this.normalizeOptionalString(crmEntry.opportunityLink) ?? this.buildOpportunityLink(opportunityId);
  }

  private buildOpportunityLink(opportunityId?: string): string | undefined {
    const normalizedOpportunityId = this.normalizeOptionalString(opportunityId);
    if (!normalizedOpportunityId) return undefined;

    try {
      const configuredCrmUrl = new URL(environment.crmUrl);
      const opportunityUrl = new URL("/main.aspx", configuredCrmUrl.origin);
      const appId = configuredCrmUrl.searchParams.get("appid");
      const forceUci = configuredCrmUrl.searchParams.get("forceUCI");

      if (appId) opportunityUrl.searchParams.set("appid", appId);
      if (forceUci) opportunityUrl.searchParams.set("forceUCI", forceUci);

      opportunityUrl.searchParams.set("pagetype", "entityrecord");
      opportunityUrl.searchParams.set("etn", "opportunity");
      opportunityUrl.searchParams.set("id", normalizedOpportunityId);

      return opportunityUrl.toString();
    } catch {
      return undefined;
    }
  }

  private normalizeOptionalString(value?: string): string | undefined {
    if (!value) return undefined;
    return value.trim() || undefined;
  }
}
